import { useEffect, useState } from "react";
import { api, errMsg, fileUrl } from "../lib/api";
import { fmtMoney, fmtNum } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { DataTable } from "../components/DataTable";
import { Modal, Field, inputCls, selectCls, btnPrimary, btnSecondary, btnDanger } from "../components/Modal";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package } from "lucide-react";
import { stockBreakdown, resolveFactors } from "../lib/units";

const EMPTY = { name: "", sku: "", barcode: "", category: "Divers", brand: "", unit: "pièce", purchase_price: "",
  sale_price: "", min_price: "", wholesale_price: "", stock: "", alert_threshold: "", supplier_id: "",
  location: "", description: "", active: true, units: [], photo_path: "" };

export default function Products() {
  const { user, hasPerm } = useAuth();
  const cur = user?.currency || "FCFA";
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ categories: [], units: [] });
  const [pg, setPg] = useState({ total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [warehouses, setWarehouses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [whId, setWhId] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const load = (p = page, s = search) =>
    api.get("/products", { params: { search: s, page: p, limit: 25 } })
      .then((r) => { setRows(r.data.items); setPg({ total: r.data.total, pages: r.data.pages }); })
      .catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    const t = setTimeout(() => load(1, search), search ? 300 : 0);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => { load(page, search); }, [page]);
  useEffect(() => {
    load();
    api.get("/products/meta").then((r) => setMeta(r.data));
    api.get("/warehouses").then((r) => { setWarehouses(r.data); if (r.data[0]) setWhId(r.data[0].id); });
    if (hasPerm("suppliers.view")) api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
  }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setPhotoFile(null); setPhotoPreview(null); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p, units: (p.units || []).map((u) => ({ ...u, _k: crypto.randomUUID(), direction: u.direction || "contains", qty: u.qty || u.factor || "" })) }); setPhotoFile(null); setPhotoPreview(null); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setU = (i, k, v) => setForm({ ...form, units: form.units.map((x, j) => (j === i ? { ...x, [k]: v } : x)) });

  const pickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const removePhoto = async () => {
    try {
      await api.delete(`/products/${editing.id}/photo`);
      setForm({ ...form, photo_path: "" });
      setPhotoPreview(null);
      toast.success("Photo supprimée");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const save = async () => {
    try {
      const num = (v) => Number(v) || 0;
      const payload = { ...form, purchase_price: num(form.purchase_price), sale_price: num(form.sale_price),
        min_price: num(form.min_price), wholesale_price: num(form.wholesale_price),
        stock: num(form.stock), alert_threshold: num(form.alert_threshold), warehouse_id: whId,
        units: form.units.filter((u) => u.unit).map((u) => ({ unit: u.unit, qty: num(u.qty),
          relative_to: u.relative_to || "", factor: num(u.factor), direction: u.direction || "per",
          is_default: !!u.is_default, sellable: u.sellable !== false,
          sale_price: num(u.sale_price), purchase_price: num(u.purchase_price) })) };
      delete payload.photo_path;
      let pid = editing?.id;
      if (editing) await api.patch(`/products/${editing.id}`, payload);
      else { const r = await api.post("/products", payload); pid = r.data.id; }
      if (photoFile && pid) {
        const fd = new FormData();
        fd.append("file", photoFile);
        await api.post(`/products/${pid}/photo`, fd);
      }
      toast.success(editing ? "Produit modifié" : "Produit créé");
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async () => {
    try {
      const r = await api.delete(`/products/${confirmDel.id}`);
      toast.success(r.data.archived ? "Produit archivé (lié à des ventes)" : "Produit supprimé");
      setConfirmDel(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const stockBadge = (p) => {
    if (p.stock <= 0) return <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300">Rupture</span>;
    if (p.alert_threshold > 0 && p.stock <= p.alert_threshold) return <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300">Faible</span>;
    return <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">En stock</span>;
  };

  const columns = [
    { key: "name", label: "Produit", render: (p) => (
      <div className="flex items-center gap-2">
        {p.photo_path
          ? <img data-testid={`product-thumb-${p.id}`} src={fileUrl(p.photo_path)} alt="" className="h-9 w-9 rounded-md object-cover border shrink-0" />
          : <div className="h-9 w-9 rounded-md bg-muted flex items-center justify-center shrink-0"><Package className="h-4 w-4 text-muted-foreground" /></div>}
        <div><div className="font-semibold">{p.name}</div><div className="text-xs text-muted-foreground">{p.sku} {p.brand && `· ${p.brand}`}</div></div>
      </div>) },
    { key: "category", label: "Catégorie" },
    ...(hasPerm("products.see_purchase_price") ? [{ key: "purchase_price", label: "Prix achat", className: "text-right", render: (p) => <span className="font-money">{fmtMoney(p.purchase_price, cur)}</span> }] : []),
    { key: "sale_price", label: "Prix vente", className: "text-right", render: (p) => <span className="font-money font-bold">{fmtMoney(p.sale_price, cur)}</span> },
    { key: "stock", label: "Stock", className: "text-right", render: (p) => (
      <div><span className="font-money">{fmtNum(p.stock)} {p.unit}</span>
        {(p.units || []).length > 0 && <div className="text-[10px] text-muted-foreground">= {stockBreakdown(p, p.stock)}</div>}
      </div>) },
    { key: "status", label: "Statut", render: (p) => p.active ? stockBadge(p) : <span className="text-xs text-muted-foreground">Inactif</span> },
    { key: "actions", label: "", className: "text-right", render: (p) => (
      <div className="flex justify-end gap-1">
        {hasPerm("products.edit") && <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-muted"><Pencil className="h-4 w-4" /></button>}
        {hasPerm("products.delete") && <button data-testid={`delete-product-${p.id}`} onClick={() => setConfirmDel(p)} className="h-8 w-8 inline-flex items-center justify-center rounded-md text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950"><Trash2 className="h-4 w-4" /></button>}
      </div>) },
  ];

  return (
    <div className="space-y-4" data-testid="products-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Produits</h1>
        {hasPerm("products.create") && <button data-testid="add-product-btn" onClick={openNew} className={btnPrimary}><Plus className="h-4 w-4" /> Nouveau produit</button>}
      </div>
      <DataTable testid="products-table" columns={columns} rows={rows} serverSide
        serverTotal={pg.total} serverPage={page} serverPages={pg.pages}
        onPageChange={setPage} onSearch={(v) => { setSearch(v); setPage(1); }} />

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Modifier le produit" : "Nouveau produit"} wide>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Désignation *"><input data-testid="product-name" required value={form.name} onChange={set("name")} className={inputCls} /></Field>
          <Field label="Catégorie">
            <select data-testid="product-category" value={form.category} onChange={set("category")} className={selectCls}>
              {meta.categories.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Référence / SKU"><input data-testid="product-sku" value={form.sku} onChange={set("sku")} className={inputCls} /></Field>
          <Field label="Code-barres"><input data-testid="product-barcode" value={form.barcode} onChange={set("barcode")} className={inputCls} /></Field>
          <Field label="Marque"><input data-testid="product-brand" value={form.brand} onChange={set("brand")} className={inputCls} /></Field>
          <Field label="Unité de base">
            <select data-testid="product-unit" value={form.unit} onChange={set("unit")} className={selectCls}>
              {meta.units.map((u) => <option key={u}>{u}</option>)}
            </select>
          </Field>
          <Field label="Prix d'achat"><input data-testid="product-purchase-price" type="number" min="0" value={form.purchase_price} onChange={set("purchase_price")} className={inputCls} /></Field>
          <Field label="Prix de vente *"><input data-testid="product-sale-price" type="number" min="0" value={form.sale_price} onChange={set("sale_price")} className={inputCls} /></Field>
          <Field label="Prix minimum"><input data-testid="product-min-price" type="number" min="0" value={form.min_price} onChange={set("min_price")} className={inputCls} /></Field>
          <Field label="Prix grossiste"><input data-testid="product-wholesale-price" type="number" min="0" value={form.wholesale_price} onChange={set("wholesale_price")} className={inputCls} /></Field>
          {!editing && <Field label="Stock initial"><input data-testid="product-stock" type="number" min="0" step="any" value={form.stock} onChange={set("stock")} className={inputCls} /></Field>}
          <Field label="Seuil d'alerte"><input data-testid="product-threshold" type="number" min="0" value={form.alert_threshold} onChange={set("alert_threshold")} className={inputCls} /></Field>
          <Field label="Fournisseur principal">
            <select data-testid="product-supplier" value={form.supplier_id} onChange={set("supplier_id")} className={selectCls}>
              <option value="">—</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Emplacement / Rayon"><input data-testid="product-location" value={form.location} onChange={set("location")} className={inputCls} /></Field>
        </div>
        <Field label="Photo du produit">
          <div className="flex items-center gap-3 flex-wrap">
            {(photoPreview || form.photo_path) && (
              <img data-testid="product-photo-preview" src={photoPreview || fileUrl(form.photo_path)} alt="" className="h-16 w-16 rounded-lg object-cover border" />)}
            <input data-testid="product-photo-input" type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={pickPhoto} className="text-sm" />
            {editing?.photo_path && !photoFile && (
              <button type="button" data-testid="product-photo-remove" onClick={removePhoto} className="text-xs text-rose-600 font-semibold hover:underline">Retirer la photo</button>)}
          </div>
          <p className="text-xs text-muted-foreground mt-1">JPG, PNG, WebP ou GIF — 5 Mo max.</p>
        </Field>
        <Field label="Description"><textarea data-testid="product-description" value={form.description} onChange={set("description")} className={`${inputCls} h-20 py-2`} /></Field>
        <div className="space-y-2 mt-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Unités de vente & conditionnements</span>
            <button type="button" data-testid="add-unit-conv" onClick={() => setForm({ ...form, units: [...form.units, { _k: crypto.randomUUID(), unit: "", qty: "", relative_to: "", direction: "per", sale_price: "", purchase_price: "" }] })} className="text-xs text-primary font-semibold hover:underline">+ Ajouter un conditionnement</button>
          </div>
          <p className="text-xs text-muted-foreground">Unité de stock : <b>{form.unit}</b>. Définissez la chaîne de conditionnements <b>propre à ce produit</b>, en descendant : 1 {form.unit} = N …, puis 1 … = N … (ex : 1 tonne = 20 sacs, 1 sac = 50 kg). Prix vide = prix calculé automatiquement (prix du parent ÷ N) ; saisissez un prix pour le remplacer.</p>
          {(() => { const uf = resolveFactors(form.units, form.unit); return form.units.map((u, i) => (
            <div key={u._k || i} className="border rounded-lg p-2 space-y-2" data-testid={`unit-row-${i}`}>
              <div className="flex items-center gap-2 flex-wrap text-sm">
                <span className="text-muted-foreground">1</span>
                {((u.direction || "per") === "per") ? (
                  <select data-testid={`unit-rel-${i}`} value={u.relative_to || ""} onChange={(e) => setU(i, "relative_to", e.target.value)} className={`${selectCls} w-32`}>
                    <option value="">{form.unit} (stock)</option>
                    {form.units.filter((x, j) => x.unit && j !== i).map((x) => <option key={x.unit} value={x.unit}>{x.unit}</option>)}
                  </select>
                ) : (
                  <input data-testid={`unit-name-${i}`} list="unit-suggestions" value={u.unit} onChange={(e) => setU(i, "unit", e.target.value)} placeholder="carton…" className={`${inputCls} w-28`} />
                )}
                <span className="text-muted-foreground">=</span>
                <input data-testid={`unit-qty-${i}`} type="number" min="0.001" step="any" value={u.qty ?? ""} onChange={(e) => setU(i, "qty", e.target.value)} className={`${inputCls} w-20`} placeholder="N" />
                {((u.direction || "per") === "per") ? (
                  <input data-testid={`unit-name-${i}`} list="unit-suggestions" value={u.unit} onChange={(e) => setU(i, "unit", e.target.value)} placeholder="sac, kg…" className={`${inputCls} w-28`} />
                ) : (
                  <span className="font-semibold" data-testid={`unit-child-${i}`}>{u.relative_to || form.unit} <span className="text-[10px] font-normal text-amber-600">(regroupant — ancien format)</span></span>
                )}
                {u.unit && uf[u.unit] != null && <span className="text-xs font-bold text-primary" data-testid={`unit-factor-${i}`}>= {fmtNum(Number(Number(uf[u.unit]).toPrecision(6)))} {form.unit}</span>}
                <button type="button" data-testid={`unit-remove-${i}`} onClick={() => setForm({ ...form, units: form.units.filter((_, j) => j !== i) })} className="text-rose-600 ml-auto"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="flex items-center gap-3 flex-wrap text-xs">
                <span className="text-muted-foreground">Prix vente / {u.unit || "unité"}</span>
                <input data-testid={`unit-sale-${i}`} type="number" min="0" value={u.sale_price ?? ""} onChange={(e) => setU(i, "sale_price", e.target.value)} className={`${inputCls} w-28 h-8`} placeholder="auto" />
                {!Number(u.sale_price) && uf[u.unit] != null && Number(form.sale_price) > 0 && (
                  <span className="text-muted-foreground" data-testid={`unit-autoprice-${i}`}>auto : {fmtNum(Math.round(Number(form.sale_price) * uf[u.unit] * 100) / 100)}</span>
                )}
                <span className="text-muted-foreground">Prix achat</span>
                <input data-testid={`unit-purchase-${i}`} type="number" min="0" value={u.purchase_price ?? ""} onChange={(e) => setU(i, "purchase_price", e.target.value)} className={`${inputCls} w-28 h-8`} placeholder="auto" />
                <label className="inline-flex items-center gap-1 font-semibold">
                  <input data-testid={`unit-default-${i}`} type="checkbox" checked={!!u.is_default}
                    onChange={(e) => setForm({ ...form, units: form.units.map((x, j) => ({ ...x, is_default: j === i ? e.target.checked : (e.target.checked ? false : x.is_default) })) })} />
                  Par défaut
                </label>
                <label className="inline-flex items-center gap-1 font-semibold">
                  <input data-testid={`unit-sellable-${i}`} type="checkbox" checked={u.sellable !== false} onChange={(e) => setU(i, "sellable", e.target.checked)} />
                  Vendable
                </label>
              </div>
            </div>
          )); })()}
          <datalist id="unit-suggestions">{meta.units.map((x) => <option key={x} value={x} />)}</datalist>
        </div>
        <div className="flex gap-2 mt-4">
          <button data-testid="save-product" onClick={save} disabled={!form.name} className={`${btnPrimary} flex-1`}>{editing ? "Enregistrer" : "Créer"}</button>
          <button onClick={() => setOpen(false)} className={btnSecondary}>Annuler</button>
        </div>
      </Modal>

      <Modal open={!!confirmDel} onClose={() => setConfirmDel(null)} title="Confirmer la suppression">
        <p className="text-sm">Supprimer <b>{confirmDel?.name}</b> ? Si le produit est lié à des ventes, il sera archivé.</p>
        <div className="flex gap-2 mt-4">
          <button data-testid="confirm-delete-product" onClick={del} className={`${btnDanger} flex-1`}>Supprimer</button>
          <button onClick={() => setConfirmDel(null)} className={btnSecondary}>Annuler</button>
        </div>
      </Modal>
    </div>
  );
}
